import { Extension } from '@tiptap/react'
import { Plugin } from '@tiptap/pm/state'

export interface ImageUploadOptions {
  uploadUrl: string
  pageId: number
}

async function uploadImage(file: File, uploadUrl: string, pageId: number): Promise<string | null> {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('page_id', String(pageId))

  const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content

  try {
    const response = await fetch(uploadUrl, {
      method: 'POST',
      headers: csrfToken ? { 'X-CSRF-Token': csrfToken } : {},
      body: formData,
    })
    if (!response.ok) return null
    const data = await response.json()
    return data.url
  } catch {
    return null
  }
}

function handleFiles(
  files: File[],
  uploadUrl: string,
  pageId: number,
  insertImage: (url: string, filename: string) => void,
) {
  for (const file of files) {
    if (!file.type.startsWith('image/')) continue
    uploadImage(file, uploadUrl, pageId).then((url) => {
      if (url) {
        insertImage(url, file.name)
      }
    })
  }
}

export const ImageUpload = Extension.create<ImageUploadOptions>({
  name: 'imageUpload',

  addOptions() {
    return {
      uploadUrl: '',
      pageId: 0,
    }
  },

  addProseMirrorPlugins() {
    const { uploadUrl, pageId } = this.options
    const editor = this.editor

    return [
      new Plugin({
        props: {
          handlePaste(_view, event) {
            const items = event.clipboardData?.items
            if (!items) return false

            const imageFiles: File[] = []
            for (const item of Array.from(items)) {
              if (item.type.startsWith('image/')) {
                const file = item.getAsFile()
                if (file) imageFiles.push(file)
              }
            }

            if (imageFiles.length === 0) return false

            event.preventDefault()
            handleFiles(imageFiles, uploadUrl, pageId, (url, alt) => {
              editor.chain().focus().setImage({ src: url, alt }).run()
            })
            return true
          },

          handleDrop(_view, event) {
            const files = event.dataTransfer?.files
            if (!files || files.length === 0) return false

            const imageFiles = Array.from(files).filter((f) => f.type.startsWith('image/'))
            if (imageFiles.length === 0) return false

            event.preventDefault()
            handleFiles(imageFiles, uploadUrl, pageId, (url, alt) => {
              editor.chain().focus().setImage({ src: url, alt }).run()
            })
            return true
          },
        },
      }),
    ]
  },
})
